import { useEffect, useMemo, useRef } from 'react';
import { useProfile } from './useProfile';
import { useCurrentSubscriptionForAccess } from './useCurrentSubscriptionForAccess';
import { useOrganization } from './useOrganization';
import { setAnalyticsUserProperties } from '../services/firebaseAnalytics';

type AnalyticsUserContext = {
  org_role: string | null;
  is_org_user: boolean;
  is_org_admin: boolean;
  subscription_status: string | null;
  subscription_plan_name: string | null;
  subscription_interval: string | null;
  has_active_subscription: boolean;
};

const isSubscriptionActiveLike = (status: string | null | undefined): boolean => {
  return status === 'active' || status === 'trialing';
};

/**
 * Sync user-specific context into GA4 user properties.
 *
 * This answers questions like "which users can do what" by letting you segment
 * all events by org role, subscription plan/status, and a few derived flags.
 *
 * Note: User properties are low-cardinality; keep them stable and small.
 */
export function useAnalyticsUserContext(): void {
  const { data: profile } = useProfile();
  const { data: organization } = useOrganization();
  const { subscription, isOrgUser, organizationRole } = useCurrentSubscriptionForAccess();

  const context: AnalyticsUserContext = useMemo(() => {
    const orgRole = organizationRole ?? profile?.organization_role ?? organization?.role ?? null;
    const subscriptionStatus = subscription?.status ?? null;
    const subscriptionPlanName = subscription?.plan_name ?? null;
    const subscriptionInterval = subscription?.interval ?? null;
    const isAdmin = orgRole === 'admin';

    return {
      org_role: orgRole,
      is_org_user: !!isOrgUser,
      is_org_admin: isAdmin,
      subscription_status: subscriptionStatus,
      subscription_plan_name: subscriptionPlanName,
      subscription_interval: subscriptionInterval,
      has_active_subscription: isSubscriptionActiveLike(subscriptionStatus),
    };
  }, [isOrgUser, organizationRole, organization?.role, profile?.organization_role, subscription]);

  // Avoid spamming GA with repeated identical property updates.
  const lastSentKeyRef = useRef<string | null>(null);
  const key = useMemo(() => JSON.stringify(context), [context]);

  useEffect(() => {
    if (lastSentKeyRef.current === key) return;
    lastSentKeyRef.current = key;

    // Firebase ignores null values; our wrapper filters them out.
    void setAnalyticsUserProperties({
      org_role: context.org_role,
      is_org_user: context.is_org_user,
      is_org_admin: context.is_org_admin,
      subscription_status: context.subscription_status,
      subscription_plan_name: context.subscription_plan_name,
      subscription_interval: context.subscription_interval,
      has_active_subscription: context.has_active_subscription,
    });
    // `key` is JSON.stringify(context); when key changes this render's context matches it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key encodes context; skip identity-only context changes
  }, [key]);
}
